/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable array-callback-return */
import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';

import { elexity } from '../../utilities/AdvancedGraphHelper';
import AuthenticatedComponent from '../../utilities/AuthenticatedComponent';
import configuration from '../../config';
import { setCurrentGX } from '../../actions';
import { connect } from 'react-redux';
import WithRouter from '../../utilities/WithRouter';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useCampusControlModes } from '../../context/CampusContextProvider';
import { useCharts } from '../../features/Charts/useCharts';
import { DATA } from '../../config/data';

require('highcharts/modules/accessibility');
require('highcharts/modules/draggable-points');
require('highcharts/modules/exporting');
require('highcharts/modules/export-data');

const EnergyUtilityMeterDetail = ({
  currentCampusData,
  currentCampusId,
  currentBuildingId,
  currentZoneId,
  currentGXId,
  hasSingleGX,
  currentGXData,
  onBuildingChange,
  onGXChange,
  onZoneChange,
  activePage,
  showMobile,
}) => {
  const [gxName, setGXName] = useState(null);
  const [chartTabActive, setChartTabActive] = useState('live');
  const [graphSubtitle, setGraphSubtitle] = useState('');
  const historyChartRef = useRef(null);
  const liveChartRef = useRef(null);
  const controlModesRef = useRef([]);

  const authenticatedComponent = new AuthenticatedComponent();

  const controlModes = useCampusControlModes() || [];
  const { applyStrategyModes, setupTooltipToggle } = useCharts();

  useEffect(() => {
    controlModesRef.current = controlModes;
    applyStrategyModes(elexity.admin.monitor.graphs.performanceLiveGraph, controlModes);
  }, [controlModes]);

  function handleRefresh() {
    elexity.admin.monitor.graphs.triggerHistoryGraphReload();
  }

  function addSeriesDefinition(available, name, color) {
    if (available) {
      graphConfiguration.seriesDefinitions.splice(2, 0, {
        name: name,
        color: color,
        type: 'spline',
        opacity: 1,
        dataGrouping: { approximation: 'average' },
      });
    } else {
      graphConfiguration.seriesDefinitions.splice(2, 0, {
        name: name,
        color: color,
        type: 'spline',
        opacity: 1,
        dataGrouping: { approximation: 'average' },
        visible: false,
      });
    }
  }

  useEffect(() => {
    if (currentGXData?.gridInterconnects) {
      currentGXData.gridInterconnects.filter((gx) => {
        if (gx.gridInterconnectDescription.gridInterconnectEntityKey === currentGXId) {
          setGXName(gx.gridInterconnectDescription.longGridInterconnectDisplayName);
        }

        if (gx.gridInterconnectDescription.gridInterconnectEntityKey === currentGXId) {
          if (historyChartRef.current && liveChartRef.current && graphSubtitle === '') {
            setGraphSubtitle(gx.gridInterconnectDescription.longGridInterconnectDisplayName);

            //Show or hide battery, solar, ev based on whether or not it's on a campus
            if (!gx.kiosk.hasBattery) {
              addSeriesDefinition(
                false,
                DATA.GX.BATTERY_CHARGE_POWER.LABEL,
                DATA.GX.BATTERY_CHARGE_POWER.COLOR
              );
              addSeriesDefinition(
                false,
                DATA.GX.BATTERY_DISCHARGE_POWER.LABEL,
                DATA.GX.BATTERY_DISCHARGE_POWER.COLOR
              );
            } else {
              addSeriesDefinition(
                true,
                DATA.GX.BATTERY_CHARGE_POWER.LABEL,
                DATA.GX.BATTERY_CHARGE_POWER.COLOR
              );
              addSeriesDefinition(
                true,
                DATA.GX.BATTERY_DISCHARGE_POWER.LABEL,
                DATA.GX.BATTERY_DISCHARGE_POWER.COLOR
              );
            }

            if (!gx.kiosk.hasSolar) {
              addSeriesDefinition(false, DATA.GX.SOLAR_POWER.LABEL, DATA.GX.SOLAR_POWER.COLOR);
            } else {
              addSeriesDefinition(true, DATA.GX.SOLAR_POWER.LABEL, DATA.GX.SOLAR_POWER.COLOR);
            }

            if (!gx.kiosk.hasEvCharger) {
              addSeriesDefinition(
                false,
                DATA.GX.EV_CHARGER_POWER.LABEL,
                DATA.GX.EV_CHARGER_POWER.COLOR
              );
            } else {
              addSeriesDefinition(
                true,
                DATA.GX.EV_CHARGER_POWER.LABEL,
                DATA.GX.EV_CHARGER_POWER.COLOR
              );
            }

            elexity.admin.monitor.graphs.initAuth(
              authenticatedComponent.generateAuthenticatedHeader()
            );
            elexity.admin.monitor.graphs.init(
              currentGXId,
              configuration['backend_host'] +
                '/admin/monitor/{entityKey}/gridInterconnect/graphData/definition',
              configuration['backend_host'] +
                '/admin/monitor/{entityKey}/gridInterconnect/graphData/history',
              configuration['backend_host'] +
                '/admin/monitor/{entityKey}/gridInterconnect/graphData/live',
              graphConfiguration,
              'historyPerformanceGraph',
              'livePerformanceGraph',
              gx.gridInterconnectDescription.longGridInterconnectDisplayName
            );
          }
        }
      });
    }
  }, [currentGXData]);

  //Load initial graphs
  let graphConfiguration = {
    seriesDefinitions: [
      {
        name: DATA.GX.GRID_POWER.LABEL,
        color: DATA.GX.GRID_POWER.COLOR,
        type: 'spline',
        opacity: 1,
        dataGrouping: { approximation: 'average' },
      },
      {
        name: DATA.GX.HVAC_POWER.LABEL,
        color: DATA.GX.HVAC_POWER.COLOR,
        type: 'spline',
        opacity: 1,
        dataGrouping: { approximation: 'average' },
      },
      {
        name: DATA.GX.TOTAL_LOAD.LABEL,
        color: DATA.GX.TOTAL_LOAD.COLOR,
        type: 'column',
        opacity: 0.35,
        dataGrouping: { approximation: 'average' },
      },
    ],

    defaultLiveZoom: 4,

    livePrimaryYAxisConfig: {
      top: '25%',
      height: '75%',
      offset: 0,
    },

    liveXAxes: [{ linkedTo: 0, visible: false }],

    liveYAxes: [
      {
        title: { text: 'Strategies' },
        categories: [],
        top: '0%',
        height: '20%',
        offset: 10,
        opposite: false,
        min: -0.5,
        max: 0.5,
        labels: { enabled: false, style: { fontSize: '9px', color: '#6b7280' } },
      },
    ],

    onLiveGraphReady: (liveGraph) => {
      applyStrategyModes(liveGraph, controlModesRef.current);
      let debounceTimer;
      liveGraph.xAxis[0].update(
        {
          events: {
            afterSetExtremes: () => {
              clearTimeout(debounceTimer);
              debounceTimer = setTimeout(() => {
                applyStrategyModes(liveGraph, controlModesRef.current);
              }, 150);
            },
          },
        },
        false
      );
      setupTooltipToggle(liveGraph);

      const histChart = elexity.admin.monitor.graphs.performanceHistoryGraph;
      if (histChart) setupTooltipToggle(histChart);
    },
  };

  return (
    <>
      <StyledMeterDetailDiv>
        {!hasSingleGX && (
          <>
            <div className={showMobile ? 'header-container mobile' : 'header-container'}>
              <div className="header">
                <h1 className="campus-name">{gxName ? gxName : 'Utility Meter'}</h1>

                {currentGXId && (
                  <Breadcrumbs
                    currentCampusData={currentCampusData}
                    currentCampusId={currentCampusId}
                    currentBuildingId={currentBuildingId}
                    currentZoneId={currentZoneId}
                    currentGXId={currentGXId}
                    hasSingleGX={hasSingleGX}
                    activePage={activePage}
                    showMobile={showMobile}
                    onBuildingChange={onBuildingChange}
                    onGXChange={onGXChange}
                    onZoneChange={onZoneChange}
                  />
                )}
              </div>
            </div>
          </>
        )}

        <div className="gx-chart-container">
          <div className="chart-nav">
            <div className="tabs">
              <div
                className={chartTabActive === 'live' ? 'live active' : 'live'}
                onClick={() => setChartTabActive('live')}
              >
                Live
              </div>
              <div
                className={chartTabActive === 'history' ? 'history active' : 'history'}
                onClick={() => setChartTabActive('history')}
              >
                History
              </div>
            </div>

            {chartTabActive === 'history' && (
              <div className="refresh" onClick={() => handleRefresh()}>
                Refresh Graph
              </div>
            )}
          </div>

          <div className="active-chart-container">
            <div
              id="historyPerformanceGraph"
              className={
                chartTabActive === 'history' ? 'performanceGraph' : 'hidden performanceGraph'
              }
              ref={historyChartRef}
            ></div>
            <div
              id="livePerformanceGraph"
              className={chartTabActive === 'live' ? 'performanceGraph' : 'hidden performanceGraph'}
              ref={liveChartRef}
            ></div>
          </div>
        </div>
      </StyledMeterDetailDiv>
    </>
  );
};

const StyledMeterDetailDiv = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: space-between;

  h1,
  h4 {
    color: #02222b;
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  h1 {
    line-height: 28px;
  }

  hr {
    background: #eaecf0;
    border: none;
    height: 2px;
    margin: 20px 0 26px;
  }

  .gx-chart-container {
    .active-chart-container {
      background: white;
      border-radius: 0 12px 12px 12px;
      box-shadow: 0px 2px 6px #02222b14;
      padding: 20px 24px 24px;

      .performanceGraph {
        height: 700px;
      }
      .hidden {
        display: none;
        height: 0;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .header-container {
      flex-flow: wrap;

      > div {
        margin-left: 0;
      }
    }
  }
`;

const mapStateToProps = (state) => {
  return {
    campuses: state.campuses,
    currentCampusId: state.currentCampusId,
    currentGXId: state.currentGXId,
    currentCampusData: state.currentCampusData,
    currentGXData: state.currentGXData,
  };
};

// used by parent class AuthenticatedComponent
const mapDispatchToProps = (dispatch) => {
  return {
    setCurrentGX: (gxId) => {
      dispatch(setCurrentGX(gxId));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WithRouter(EnergyUtilityMeterDetail));
